/**
 * Pure helpers + static dictionary for `HttpStatusTool`. Tests live in
 * `tests/web/httpStatusTool.test.ts`.
 *
 * Coverage: every IANA-registered standard HTTP status code as of
 * RFC 9110 (2022). Cloudflare / nginx custom codes (520-527, 444,
 * etc.) are intentionally NOT here — they're on the P1 roadmap as
 * an "extended" toggle so the default list stays focused.
 */

export type StatusCategory = '1xx' | '2xx' | '3xx' | '4xx' | '5xx';

export interface StatusDef {
  code: number;
  /** Canonical English name from the RFC, e.g. "Not Found". */
  name: string;
  /** Common Chinese translation; used for full-text search alongside `name`. */
  nameZh: string;
  /** One-paragraph "when to use / what it means" description. */
  description: string;
  /** Latest IETF document defining the code. */
  rfc?: string;
  /** Bucket; computed for free from `code` but stored to avoid recomputing in render. */
  category: StatusCategory;
}

/** Prefix of every IETF datatracker URL we link to. */
export const RFC_BASE = 'https://datatracker.ietf.org/doc/html/';

const RAW: Omit<StatusDef, 'category'>[] = [
  // ---- 1xx ----
  { code: 100, name: 'Continue', nameZh: '继续', description: '客户端应继续发送请求体；常见于 100-continue 协商。', rfc: 'rfc9110#section-15.2.1' },
  { code: 101, name: 'Switching Protocols', nameZh: '协议切换', description: '服务器同意切换协议（如 HTTP → WebSocket）。', rfc: 'rfc9110#section-15.2.2' },
  { code: 102, name: 'Processing', nameZh: '处理中', description: '服务器已收到请求并在处理；WebDAV 扩展。', rfc: 'rfc2518' },
  { code: 103, name: 'Early Hints', nameZh: '提前提示', description: '让客户端先开始预连接 / 预加载资源（preload / preconnect）。', rfc: 'rfc8297' },

  // ---- 2xx ----
  { code: 200, name: 'OK', nameZh: '成功', description: '请求成功。GET / POST / PUT 都能用。', rfc: 'rfc9110#section-15.3.1' },
  { code: 201, name: 'Created', nameZh: '已创建', description: '资源已创建；推荐在 Location 头返回新资源 URI。', rfc: 'rfc9110#section-15.3.2' },
  { code: 202, name: 'Accepted', nameZh: '已接受', description: '请求已接受但尚未处理完；适合异步任务。', rfc: 'rfc9110#section-15.3.3' },
  { code: 204, name: 'No Content', nameZh: '无内容', description: '成功但没有响应体；DELETE / PUT 常用。', rfc: 'rfc9110#section-15.3.5' },
  { code: 205, name: 'Reset Content', nameZh: '重置内容', description: '让客户端重置表单或视图。', rfc: 'rfc9110#section-15.3.6' },
  { code: 206, name: 'Partial Content', nameZh: '部分内容', description: '断点续传 / 范围请求成功；配合 Range 头。', rfc: 'rfc9110#section-15.3.7' },
  { code: 207, name: 'Multi-Status', nameZh: '多状态', description: 'WebDAV：请求体内含多个子操作的状态。', rfc: 'rfc4918' },
  { code: 208, name: 'Already Reported', nameZh: '已报告', description: 'WebDAV：避免重复上报同一资源。', rfc: 'rfc5842' },
  { code: 226, name: 'IM Used', nameZh: '已应用实例操作', description: '服务器用 delta encoding 满足请求。', rfc: 'rfc3229' },

  // ---- 3xx ----
  { code: 300, name: 'Multiple Choices', nameZh: '多种选择', description: '请求资源对应多个表示，由客户端选择。', rfc: 'rfc9110#section-15.4.1' },
  { code: 301, name: 'Moved Permanently', nameZh: '永久重定向', description: '资源永久移走；缓存与 SEO 都会跟随；通常方法保持。', rfc: 'rfc9110#section-15.4.2' },
  { code: 302, name: 'Found', nameZh: '临时重定向', description: '历史含义混乱：浏览器多数把 POST 重写为 GET。新 API 推荐用 307 / 303。', rfc: 'rfc9110#section-15.4.3' },
  { code: 303, name: 'See Other', nameZh: '查看其他', description: 'POST 之后用 GET 跳转到结果页；PRG 模式标准。', rfc: 'rfc9110#section-15.4.4' },
  { code: 304, name: 'Not Modified', nameZh: '未修改', description: '配合 ETag / If-Modified-Since；客户端可继续用本地缓存。', rfc: 'rfc9110#section-15.4.5' },
  { code: 307, name: 'Temporary Redirect', nameZh: '临时重定向', description: '与 302 类似，但严格保留请求方法。', rfc: 'rfc9110#section-15.4.8' },
  { code: 308, name: 'Permanent Redirect', nameZh: '永久重定向', description: '与 301 类似，但严格保留请求方法。', rfc: 'rfc7538' },

  // ---- 4xx ----
  { code: 400, name: 'Bad Request', nameZh: '请求错误', description: '请求语法错误；body 解析失败、参数缺失等。', rfc: 'rfc9110#section-15.5.1' },
  { code: 401, name: 'Unauthorized', nameZh: '未认证', description: '没有或凭证无效。注意：与 403 区分 — 401 是没登录，403 是登录但没权限。响应必须带 WWW-Authenticate。', rfc: 'rfc9110#section-15.5.2' },
  { code: 402, name: 'Payment Required', nameZh: '需要付费', description: '保留状态码；目前没有标准用法。', rfc: 'rfc9110#section-15.5.3' },
  { code: 403, name: 'Forbidden', nameZh: '禁止访问', description: '已知道你是谁但你无权访问；不应再要求重新登录。', rfc: 'rfc9110#section-15.5.4' },
  { code: 404, name: 'Not Found', nameZh: '资源不存在', description: '资源不存在；某些场景也用作"权限不足且不想暴露资源存在"。', rfc: 'rfc9110#section-15.5.5' },
  { code: 405, name: 'Method Not Allowed', nameZh: '方法不允许', description: '资源不支持当前 HTTP 方法；响应必须带 Allow 头。', rfc: 'rfc9110#section-15.5.6' },
  { code: 406, name: 'Not Acceptable', nameZh: '不可接受', description: '内容协商失败；服务器无法返回 Accept 要求的表示。', rfc: 'rfc9110#section-15.5.7' },
  { code: 407, name: 'Proxy Authentication Required', nameZh: '需要代理认证', description: '与 401 类似，但是代理在要求认证。', rfc: 'rfc9110#section-15.5.8' },
  { code: 408, name: 'Request Timeout', nameZh: '请求超时', description: '客户端在服务端等待时间内未发完请求。', rfc: 'rfc9110#section-15.5.9' },
  { code: 409, name: 'Conflict', nameZh: '冲突', description: '资源当前状态与请求冲突；如版本冲突 / 唯一约束。', rfc: 'rfc9110#section-15.5.10' },
  { code: 410, name: 'Gone', nameZh: '已删除', description: '资源永久删除；与 404 的区别是 410 明确表示"曾经有过"。', rfc: 'rfc9110#section-15.5.11' },
  { code: 411, name: 'Length Required', nameZh: '需要长度', description: '缺少 Content-Length 头。', rfc: 'rfc9110#section-15.5.12' },
  { code: 412, name: 'Precondition Failed', nameZh: '前置条件失败', description: 'If-Match / If-None-Match 等条件失败。', rfc: 'rfc9110#section-15.5.13' },
  { code: 413, name: 'Content Too Large', nameZh: '请求体过大', description: '请求体超过服务器限制；旧名 "Payload Too Large"。', rfc: 'rfc9110#section-15.5.14' },
  { code: 414, name: 'URI Too Long', nameZh: 'URI 过长', description: 'URI 超过服务器限制；通常发生在 GET 携带过多查询。', rfc: 'rfc9110#section-15.5.15' },
  { code: 415, name: 'Unsupported Media Type', nameZh: '不支持的媒体类型', description: '请求体的 Content-Type 不被支持。', rfc: 'rfc9110#section-15.5.16' },
  { code: 416, name: 'Range Not Satisfiable', nameZh: '范围不满足', description: 'Range 头超出资源大小。', rfc: 'rfc9110#section-15.5.17' },
  { code: 417, name: 'Expectation Failed', nameZh: '预期失败', description: 'Expect 头无法满足。', rfc: 'rfc9110#section-15.5.18' },
  { code: 418, name: "I'm a teapot", nameZh: '我是茶壶', description: '彩蛋；RFC 2324 愚人节笑话。', rfc: 'rfc2324' },
  { code: 421, name: 'Misdirected Request', nameZh: '请求错路了', description: 'HTTP/2 多 origin 时常见：被路由到了不正确的服务器。', rfc: 'rfc9110#section-15.5.20' },
  { code: 422, name: 'Unprocessable Content', nameZh: '不可处理实体', description: '语义错误（语法 OK 但业务校验失败）；REST API 验证常用。', rfc: 'rfc9110#section-15.5.21' },
  { code: 423, name: 'Locked', nameZh: '已锁定', description: 'WebDAV：资源被锁。', rfc: 'rfc4918' },
  { code: 424, name: 'Failed Dependency', nameZh: '依赖失败', description: 'WebDAV：先决请求失败导致本请求失败。', rfc: 'rfc4918' },
  { code: 425, name: 'Too Early', nameZh: '太早了', description: 'TLS 1.3 0-RTT 数据被拒绝以防重放。', rfc: 'rfc8470' },
  { code: 426, name: 'Upgrade Required', nameZh: '需要升级', description: '客户端必须升级协议；响应需带 Upgrade 头。', rfc: 'rfc9110#section-15.5.22' },
  { code: 428, name: 'Precondition Required', nameZh: '需要前置条件', description: '强制客户端使用条件请求避免 lost-update。', rfc: 'rfc6585' },
  { code: 429, name: 'Too Many Requests', nameZh: '请求过多', description: '限流；推荐配 Retry-After 头。', rfc: 'rfc6585' },
  { code: 431, name: 'Request Header Fields Too Large', nameZh: '请求头过大', description: '某个 / 全部请求头字段超过服务器限制。', rfc: 'rfc6585' },
  { code: 451, name: 'Unavailable For Legal Reasons', nameZh: '因法律原因不可用', description: '法律 / 监管要求屏蔽资源（取自 Fahrenheit 451）。', rfc: 'rfc7725' },

  // ---- 5xx ----
  { code: 500, name: 'Internal Server Error', nameZh: '服务器内部错误', description: '服务端默认兜底错误；通常代表代码 bug 或未捕获异常。', rfc: 'rfc9110#section-15.6.1' },
  { code: 501, name: 'Not Implemented', nameZh: '未实现', description: '服务器不支持请求的功能（如未实现的 HTTP 方法）。', rfc: 'rfc9110#section-15.6.2' },
  { code: 502, name: 'Bad Gateway', nameZh: '网关错误', description: '代理 / 网关从上游收到无效响应。', rfc: 'rfc9110#section-15.6.3' },
  { code: 503, name: 'Service Unavailable', nameZh: '服务不可用', description: '临时不可用（过载 / 维护）；可配 Retry-After。', rfc: 'rfc9110#section-15.6.4' },
  { code: 504, name: 'Gateway Timeout', nameZh: '网关超时', description: '代理 / 网关在等待上游时超时。', rfc: 'rfc9110#section-15.6.5' },
  { code: 505, name: 'HTTP Version Not Supported', nameZh: 'HTTP 版本不支持', description: '请求所用的 HTTP 版本服务器不支持。', rfc: 'rfc9110#section-15.6.6' },
  { code: 506, name: 'Variant Also Negotiates', nameZh: '变体亦在协商', description: '透明内容协商配置错误。', rfc: 'rfc2295' },
  { code: 507, name: 'Insufficient Storage', nameZh: '存储不足', description: 'WebDAV：服务器存储空间不足。', rfc: 'rfc4918' },
  { code: 508, name: 'Loop Detected', nameZh: '检测到循环', description: 'WebDAV：处理过程中检测到无限循环。', rfc: 'rfc5842' },
  { code: 510, name: 'Not Extended', nameZh: '未扩展', description: '服务器需要扩展才能完成请求。', rfc: 'rfc2774' },
  { code: 511, name: 'Network Authentication Required', nameZh: '需要网络认证', description: '需要先通过中间网关认证（典型：公共 WiFi captive portal）。', rfc: 'rfc6585' },
];

export const HTTP_STATUSES: StatusDef[] = RAW.map((s) => ({
  ...s,
  category: codeToCategory(s.code),
}));

function codeToCategory(code: number): StatusCategory {
  if (code < 200) return '1xx';
  if (code < 300) return '2xx';
  if (code < 400) return '3xx';
  if (code < 500) return '4xx';
  return '5xx';
}

export interface SearchOptions {
  /** Subset of categories to include. Empty / `undefined` = all. */
  categories?: ReadonlySet<StatusCategory> | StatusCategory[];
}

/**
 * Search the dictionary by code, English name or Chinese name.
 * Empty query returns the full list (subject to `categories` filter).
 *
 * Numeric queries match `code` only — typing `40` does NOT also pull
 * in "Forbidden" because that's just noise.
 */
export function searchStatuses(query: string, opts: SearchOptions = {}): StatusDef[] {
  const cats = normaliseCategories(opts.categories);
  const filtered =
    cats.size === 5 ? HTTP_STATUSES : HTTP_STATUSES.filter((s) => cats.has(s.category));

  const q = query.trim();
  if (q.length === 0) return filtered;

  if (/^\d+$/.test(q)) {
    const n = Number(q);
    return filtered.filter((s) => String(s.code).startsWith(String(n)));
  }

  const qLower = q.toLowerCase();
  return filtered.filter(
    (s) =>
      s.name.toLowerCase().includes(qLower) ||
      s.nameZh.includes(q) ||
      s.description.includes(q) ||
      s.description.toLowerCase().includes(qLower),
  );
}

function normaliseCategories(
  c?: ReadonlySet<StatusCategory> | StatusCategory[],
): Set<StatusCategory> {
  const all: StatusCategory[] = ['1xx', '2xx', '3xx', '4xx', '5xx'];
  if (!c) return new Set(all);
  // `c instanceof Set` doesn't narrow `ReadonlySet | Array` cleanly
  // because both are structurally compatible — fall back to `Array.isArray`.
  if (Array.isArray(c)) {
    return c.length === 0 ? new Set(all) : new Set(c);
  }
  return c.size === 0 ? new Set(all) : new Set(c);
}
